import { ONBOARDING_STEP_IDS, ONBOARDING_SUBSTEP_IDS, STEP_ORDER, ROUTE_PATHS } from "./constants";

export interface WorkflowTrackerNavigationOptions {
  countryOfIncorporation?: string;
  navigateCompanyCreation: (path: string) => void;
  navigateFundingApp: (path: string) => void;
  completedStepIds?: string[];
  completedSubstepIds?: string[];
  currentStepId?: string;
  currentSubstepId?: string;
}

export function useWorkflowTrackerNavigation({
  countryOfIncorporation,
  navigateCompanyCreation,
  navigateFundingApp,
  completedStepIds = [],
  completedSubstepIds = [],
  currentStepId,
  currentSubstepId,
}: WorkflowTrackerNavigationOptions) {
  const isStateRequired = countryOfIncorporation === "US" || countryOfIncorporation === "CA";

  const handleNavigateToStep = (targetStepId: string, targetSubstepId?: string) => {
    if (
      !isNavigationAllowed(
        targetStepId,
        targetSubstepId,
        currentStepId,
        currentSubstepId,
        completedStepIds,
        completedSubstepIds,
        isStateRequired
      )
    ) {
      return;
    }

    if (targetStepId === ONBOARDING_STEP_IDS.BUSINESS_COUNTRY) {
      navigateCompanyCreation(ROUTE_PATHS.BUSINESS_COUNTRY);
    } else if (targetStepId === ONBOARDING_STEP_IDS.MONTHLY_REVENUE) {
      navigateCompanyCreation(ROUTE_PATHS.REVENUE);
    } else if (targetStepId === ONBOARDING_STEP_IDS.BUSINESS_DETAILS) {
      if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION) {
        navigateCompanyCreation(ROUTE_PATHS.BUSINESS_LOCATION);
      } else if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION) {
        navigateCompanyCreation(ROUTE_PATHS.BUSINESS_DETAILS);
      } else if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL) {
        navigateFundingApp(ROUTE_PATHS.BUSINESS_MODEL);
      }
    } else if (targetStepId === ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA) {
      if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT) {
        navigateFundingApp(ROUTE_PATHS.CONNECTIONS_BANKING);
      } else if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.PLATFORMS) {
        navigateFundingApp(ROUTE_PATHS.CONNECTIONS_ADD);
      } else if (targetSubstepId === ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK) {
        navigateFundingApp(ROUTE_PATHS.CONSENT_CREDIT_CHECK);
      } else if (targetSubstepId) {
        navigateFundingApp(`${ROUTE_PATHS.UPLOADS_DOCUMENT}${targetSubstepId}`);
      } else {
        navigateFundingApp(ROUTE_PATHS.CONNECTIONS_ADD);
      }
    } else if (targetStepId === ONBOARDING_STEP_IDS.APPLICATION_REVIEW) {
      navigateFundingApp(ROUTE_PATHS.SUMMARY);
    }
  };

  return { handleNavigateToStep, isStateRequired };
}

function isNavigationAllowed(
  targetStepId: string,
  targetSubstepId: string | undefined,
  currentStepId: string | undefined,
  currentSubstepId: string | undefined,
  completedStepIds: string[],
  completedSubstepIds: string[],
  isStateRequired: boolean
): boolean {
  const isTargetCurrent = targetStepId === currentStepId && targetSubstepId === currentSubstepId;
  if (isTargetCurrent) {
    return false;
  }

  const isTargetCompleted = targetSubstepId
    ? completedSubstepIds.includes(targetSubstepId)
    : completedStepIds.includes(targetStepId);

  if (targetSubstepId && targetStepId === ONBOARDING_STEP_IDS.BUSINESS_DETAILS) {
    return (
      isNavigationAllowedToStep(targetStepId, currentStepId, completedStepIds) &&
      isNavigationAllowedToBusinessDetailsSubstep(
        targetSubstepId,
        isStateRequired,
        isTargetCompleted,
        completedSubstepIds
      )
    );
  }

  if (targetSubstepId && targetStepId === ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA) {
    return isNavigationAllowedToConnectionsSubstep(isTargetCompleted, currentStepId);
  }

  return isNavigationAllowedToStep(targetStepId, currentStepId, completedStepIds);
}

function findFurthestCompletedIndex(order: string[], completedIds: string[]): number {
  for (let i = order.length - 1; i >= 0; i--) {
    if (completedIds.includes(order[i])) {
      return i;
    }
  }
  return -1;
}

function isNavigationAllowedToStep(
  targetStepId: string,
  currentStepId: string | undefined,
  completedStepIds: string[]
): boolean {
  const isTargetCurrent = targetStepId === currentStepId;
  const isTargetCompleted = completedStepIds.includes(targetStepId);

  const furthestCompletedIndex = findFurthestCompletedIndex(STEP_ORDER, completedStepIds);
  const nextAvailableIndex = furthestCompletedIndex + 1;
  const targetIndex = STEP_ORDER.indexOf(targetStepId);
  const isNextAvailable = targetIndex === nextAvailableIndex;

  return isTargetCurrent || isTargetCompleted || isNextAvailable;
}

function isNavigationAllowedToBusinessDetailsSubstep(
  targetSubstepId: string,
  isStateRequired: boolean,
  isTargetCompleted: boolean,
  completedSubstepIds: string[]
): boolean {
  const substepOrder: string[] = [
    ...(isStateRequired ? [ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION] : []),
    ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION,
    ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL,
  ];

  const furthestCompletedIndex = findFurthestCompletedIndex(substepOrder, completedSubstepIds);
  const nextAvailableIndex = furthestCompletedIndex + 1;
  const targetIndex = substepOrder.indexOf(targetSubstepId);
  const isNextAvailable = targetIndex === nextAvailableIndex;

  return isTargetCompleted || isNextAvailable;
}

function isNavigationAllowedToConnectionsSubstep(
  isTargetCompleted: boolean,
  currentStepId: string | undefined
): boolean {
  const isInConnectionsStep = currentStepId === ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA;
  return isTargetCompleted || isInConnectionsStep;
}
